import { Head, Link } from "@inertiajs/react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Flex,
  Section,
  Text,
  Theme,
} from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { AlertCircle, PanelRightClose, X } from "lucide-react";
import {
  ManagerBlogsIndexBlog,
  ManagerBlogsIndexProps,
} from "@/types/manager_blogs";
import BlogsTable from "@/shared/tables/manager/BlogsTable";
import ManagerPageHeader from "@/shared/manager/ManagerPageHeader";
import BlogDetailPanel from "@/shared/manager/BlogDetailPanel";
import ExpandableCards from "@/shared/manager/ExpandableCards";
import useIsMobile from "@/shared/hooks/useIsMobile";
import { badgeColors } from "@/shared/constants/constants";

export default function ManagerBlogsIndex({
  blogs,
  denial,
  paths,
}: ManagerBlogsIndexProps) {
  const isMobile = useIsMobile();
  const [selectedRow, setSelectedRow] = useState<ManagerBlogsIndexBlog | null>(
    null,
  );

  // Keeps the open drawer pointing at the row's new props after a partial
  // reload, which is what lets the status badge flip under a one-click publish
  // without the drawer closing.
  useEffect(() => {
    if (selectedRow) {
      const updated = blogs.find((b) => b.id === selectedRow.id);
      if (updated) setSelectedRow(updated);
    }
  }, [blogs]);

  // Reaching this page with blogs switched off explains itself rather than
  // bouncing to root — the same call the gallery page makes.
  const refusal = denial && (
    <Callout.Root color="amber" role="status" mb="4">
      <Callout.Icon>
        <AlertCircle />
      </Callout.Icon>
      <Callout.Text>{denial}</Callout.Text>
    </Callout.Root>
  );

  const newButton = !denial && (
    <Button asChild>
      <Link href={paths.new}>New Post</Link>
    </Button>
  );

  if (isMobile) {
    return (
      <div className="flex">
        <Head title="My Blog" />
        <div className="grow min-w-0">
          <ManagerPageHeader title="Blog">{newButton || undefined}</ManagerPageHeader>
          <Section size="1">
            <Box px="3">
              {refusal}
              <ExpandableCards
                items={blogs}
                getKey={(blog) => blog.id}
                emptyMessage="You haven't written any posts yet."
                renderSummary={(blog) => (
                  <Flex direction="column" gap="1">
                    <p className="truncate text-[15px] font-bold leading-tight">
                      {blog.title}
                    </p>
                    <span className="truncate text-sm text-gray11">
                      {blog.author}
                      {blog.publish_at ? ` · ${blog.publish_at}` : ""}
                    </span>
                    <Flex gap="2" mt="1" wrap="wrap">
                      <Badge
                        variant="soft"
                        color={badgeColors[blog.status]}
                        style={{ textTransform: "capitalize" }}
                      >
                        {blog.status}
                      </Badge>
                    </Flex>
                  </Flex>
                )}
                renderDetail={(blog) => <BlogDetailPanel blog={blog} compact />}
              />
            </Box>
          </Section>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Head title="My Blog" />
      <div className="grow min-w-0">
        <ManagerPageHeader title="Blog">{newButton || undefined}</ManagerPageHeader>
        <Dialog.Root
          open={selectedRow !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedRow(null);
          }}
        >
          <Section size="1">
            <Box px="3">{refusal}</Box>
            <Box style={{ overflowX: "auto" }}>
              <BlogsTable blogs={blogs} setSelectedRow={setSelectedRow} />
            </Box>
          </Section>

          <Dialog.Portal>
            <Theme>
              <Dialog.Overlay
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "var(--color-overlay)",
                }}
              />
              <Dialog.Content
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  width: "min(440px, 100vw)",
                  height: "100vh",
                  background: "var(--color-panel-solid)",
                  padding: "var(--space-5)",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                <Flex justify="between" align="center">
                  <Dialog.Title asChild>
                    <Text size="5" weight="bold">
                      Post Detail
                    </Text>
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <Button variant="ghost" size="1">
                      <X size="16px" />
                    </Button>
                  </Dialog.Close>
                </Flex>
                {selectedRow && <BlogDetailPanel blog={selectedRow} />}
                <Dialog.Close asChild>
                  <Button
                    variant="soft"
                    size="3"
                    className="sm:hidden"
                    style={{
                      position: "sticky",
                      bottom: 0,
                      alignSelf: "flex-end",
                      marginTop: "auto",
                      backgroundColor: "var(--color-panel-solid)",
                    }}
                  >
                    <PanelRightClose size="16px" />
                    Close
                  </Button>
                </Dialog.Close>
              </Dialog.Content>
            </Theme>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </div>
  );
}
